import { EmlSelection } from '../EmlImportTabContent'
import { buildImageSelection, ImageSelection } from '../ImageImportTabContent'
import { PdfSelection } from '../PdfImportTabContent'

import {
  detectReferenceFileType,
  EnabledReferenceFileTypes,
  ReferenceFileType,
} from './detectReferenceFileType'
import { extractEmlContent } from './extractEmlContent'
import { extractPdfText } from './extractPdfText'
import { renderPdfThumbnail } from './renderPdfThumbnail'

const MAX_PDF_SIZE_BYTES = 20971520 // 20MB
const MAX_IMAGE_SIZE_BYTES = 6291456 // 6MB
const MAX_EML_SIZE_BYTES = 15728640 // 15MB

const extractTextSafely = async (file: File) => {
  try {
    const buffer = await file.arrayBuffer()
    return { success: true as const, text: await extractPdfText(buffer) }
  } catch {
    return { success: false as const, text: null }
  }
}

const renderThumbnailSafely = async (file: File) => {
  try {
    const buffer = await file.arrayBuffer()
    return await renderPdfThumbnail(buffer)
  } catch {
    // Thumbnail is best-effort — a render failure shouldn't block import as
    // long as text extraction succeeded.
    return null
  }
}

const maxSizeForType = (type: ReferenceFileType): number => {
  if (type === 'pdf') {
    return MAX_PDF_SIZE_BYTES
  }
  if (type === 'image') {
    return MAX_IMAGE_SIZE_BYTES
  }
  return MAX_EML_SIZE_BYTES
}

const disabledTypeMessage = (type: ReferenceFileType): string => {
  if (type === 'pdf') {
    return 'PDF import is not enabled.'
  }
  if (type === 'image') {
    return 'Image import is not enabled.'
  }
  return 'Email import is not enabled.'
}

export type ProcessReferenceFileSuccess =
  | { kind: 'pdf'; selection: PdfSelection }
  | { kind: 'image'; selection: ImageSelection }
  | { kind: 'eml'; selection: EmlSelection }

export type ProcessReferenceFileResult =
  | { ok: true; value: ProcessReferenceFileSuccess }
  | { ok: false; error: string }

export const processReferenceFile = async (
  file: File,
  enabledFileTypes: EnabledReferenceFileTypes,
  onProcessing: (kind: ReferenceFileType, file: File) => void
): Promise<ProcessReferenceFileResult> => {
  const detected = detectReferenceFileType(file)

  if (!detected) {
    return {
      ok: false,
      error: 'This file type is not supported. Use PDF, EML, JPG, or PNG.',
    }
  }

  if (!enabledFileTypes[detected]) {
    return { ok: false, error: disabledTypeMessage(detected) }
  }

  if (file.size > maxSizeForType(detected)) {
    return {
      ok: false,
      error: 'This file is too large. Please try a smaller file.',
    }
  }

  onProcessing(detected, file)

  if (detected === 'pdf') {
    const [textOutcome, thumbnailUrl] = await Promise.all([
      extractTextSafely(file),
      renderThumbnailSafely(file),
    ])

    if (!textOutcome.success || !textOutcome.text?.trim()) {
      return {
        ok: false,
        error: textOutcome.success
          ? 'No readable text was found in this PDF. Try a different file.'
          : 'Could not read this PDF. Please try a different file.',
      }
    }

    return {
      ok: true,
      value: {
        kind: 'pdf',
        selection: {
          file,
          thumbnailUrl,
          extractedText: textOutcome.text,
          isProcessing: false,
          error: null,
        },
      },
    }
  }

  if (detected === 'image') {
    const selection = await buildImageSelection(file)
    if (!selection.dataUri) {
      if (selection.previewUrl) {
        URL.revokeObjectURL(selection.previewUrl)
      }
      return {
        ok: false,
        error:
          selection.error ??
          'Could not read this image. Please try a different file.',
      }
    }
    return { ok: true, value: { kind: 'image', selection } }
  }

  try {
    const buffer = await file.arrayBuffer()
    const { subject, html: emlHtml } = await extractEmlContent(buffer)

    if (!emlHtml) {
      return {
        ok: false,
        error: 'No HTML content was found in this email. Try a different file.',
      }
    }

    return {
      ok: true,
      value: {
        kind: 'eml',
        selection: {
          file,
          subject,
          html: emlHtml,
          isProcessing: false,
          error: null,
        },
      },
    }
  } catch {
    return {
      ok: false,
      error: 'Could not read this email. Please try a different file.',
    }
  }
}
